import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Heart, Calendar, Stethoscope, Syringe,
  FileText, Plus, Search, Filter, Eye, Edit, Trash2,
  Phone, Mail, MapPin, Download, Printer, CheckCircle,
  Clock, PawPrint, CreditCard, ArrowRight, Menu, Moon, PlusCircle, ClipboardPlus,
  Home, Building2, BarChart3,
} from 'lucide-react';
import { useSeo } from '../utils/useSeo';

const demoData = {
  stats: { totalOwners: 156, totalPets: 243, todayAppointments: 12, pendingExaminations: 8 },
  recentAppointments: [
    { id: 1, time: '09:00', owner: 'Ahmet Yılmaz', pet: 'Karamel', type: 'Kontrol' },
    { id: 2, time: '10:30', owner: 'Ayşe Demir', pet: 'Pamuk', type: 'Aşı' },
    { id: 3, time: '14:00', owner: 'Mehmet Kaya', pet: 'Simba', type: 'Muayene' },
    { id: 4, time: '15:30', owner: 'Fatma Özkan', pet: 'Luna', type: 'Kontrol' },
  ],
  pets: [
    { id: 1, name: 'Karamel', breed: 'Golden Retriever', age: 3, owner: 'Ahmet Yılmaz', gender: 'Erkek', weight: 28.5, avatarColor: 'from-amber-400 to-orange-500' },
    { id: 2, name: 'Pamuk', breed: 'British Shorthair', age: 2, owner: 'Ayşe Demir', gender: 'Dişi', weight: 4.2, avatarColor: 'from-teal-400 to-teal-600' },
    { id: 3, name: 'Simba', breed: 'Tekir', age: 1, owner: 'Mehmet Kaya', gender: 'Erkek', weight: 3.8, avatarColor: 'from-orange-400 to-yellow-500' },
    { id: 4, name: 'Luna', breed: 'Husky', age: 4, owner: 'Fatma Özkan', gender: 'Dişi', weight: 22.0, avatarColor: 'from-blue-400 to-blue-600' },
    { id: 5, name: 'Max', breed: 'Labrador', age: 5, owner: 'Can Arslan', gender: 'Erkek', weight: 32.1, avatarColor: 'from-amber-600 to-yellow-700' },
    { id: 6, name: 'Bella', breed: 'Persian', age: 3, owner: 'Zeynep Kara', gender: 'Dişi', weight: 5.1, avatarColor: 'from-purple-400 to-purple-600' },
  ],
  owners: [
    { id: 1, name: 'Ahmet Yılmaz', phone: '0532 123 45 67', email: 'ahmet@email.com', address: 'Kadıköy, İstanbul', petCount: 2 },
    { id: 2, name: 'Ayşe Demir', phone: '0533 234 56 78', email: 'ayse@email.com', address: 'Beşiktaş, İstanbul', petCount: 1 },
    { id: 3, name: 'Mehmet Kaya', phone: '0534 345 67 89', email: 'mehmet@email.com', address: 'Şişli, İstanbul', petCount: 1 },
    { id: 4, name: 'Fatma Özkan', phone: '0535 456 78 90', email: 'fatma@email.com', address: 'Üsküdar, İstanbul', petCount: 1 },
    { id: 5, name: 'Can Arslan', phone: '0536 567 89 01', email: 'can@email.com', address: 'Bakırköy, İstanbul', petCount: 1 },
    { id: 6, name: 'Zeynep Kara', phone: '0537 678 90 12', email: 'zeynep@email.com', address: 'Maltepe, İstanbul', petCount: 1 },
  ],
  vaccinations: [
    { id: 1, petId: 1, petName: 'Karamel', vaccine: 'Karma Aşı', date: '2024-01-15', nextDate: '2025-01-15', status: 'completed' },
    { id: 2, petId: 1, petName: 'Karamel', vaccine: 'Kuduz', date: '2024-02-10', nextDate: '2025-02-10', status: 'completed' },
    { id: 3, petId: 2, petName: 'Pamuk', vaccine: 'Karma Aşı', date: '2024-03-05', nextDate: '2025-03-05', status: 'completed' },
    { id: 4, petId: 3, petName: 'Simba', vaccine: 'Kuduz', date: '2024-12-01', nextDate: '2025-12-01', status: 'upcoming' },
    { id: 5, petId: 4, petName: 'Luna', vaccine: 'Karma Aşı', date: '2024-01-20', nextDate: '2025-01-20', status: 'completed' },
  ],
  appointments: [
    { id: 1, date: '2025-01-06', time: '09:00', petName: 'Karamel', ownerName: 'Ahmet Yılmaz', type: 'Kontrol Muayenesi', status: 'confirmed', notes: 'Yıllık kontrol' },
    { id: 2, date: '2025-01-06', time: '10:30', petName: 'Pamuk', ownerName: 'Ayşe Demir', type: 'Aşı Randevusu', status: 'confirmed', notes: 'Karma aşısı' },
    { id: 3, date: '2025-01-06', time: '14:00', petName: 'Simba', ownerName: 'Mehmet Kaya', type: 'Genel Muayene', status: 'pending', notes: 'Kusma şikayeti' },
    { id: 4, date: '2025-01-07', time: '09:30', petName: 'Luna', ownerName: 'Fatma Özkan', type: 'Kontrol', status: 'confirmed', notes: 'Ameliyat sonrası kontrol' },
    { id: 5, date: '2025-01-07', time: '11:00', petName: 'Max', ownerName: 'Can Arslan', type: 'Aşı', status: 'confirmed', notes: 'Kuduz aşısı' },
  ],
  examinations: [
    { id: 1, date: '2025-01-05', petName: 'Karamel', ownerName: 'Ahmet Yılmaz', complaint: 'Yıllık kontrol muayenesi', diagnosis: 'Sağlıklı', treatment: 'Vitamin takviyeleri önerildi', status: 'completed', weight: 28.5, temperature: 38.2 },
    { id: 2, date: '2025-01-04', petName: 'Pamuk', ownerName: 'Ayşe Demir', complaint: 'İştahsızlık ve uyku hali', diagnosis: 'Hafif sindirim sorunu', treatment: 'Diyet değişikliği ve probiyotik', status: 'completed', weight: 4.2, temperature: 38.8 },
    { id: 3, date: '2025-01-03', petName: 'Luna', ownerName: 'Fatma Özkan', complaint: 'Ameliyat sonrası kontrol', diagnosis: 'Ameliyat bölgesi iyileşmiş', treatment: 'Antibiyotik tedavisi tamamlandı', status: 'completed', weight: 22.0, temperature: 38.1 },
    { id: 4, date: '2025-01-02', petName: 'Simba', ownerName: 'Mehmet Kaya', complaint: 'Kusma ve halsizlik', diagnosis: 'Gastrit', treatment: 'Mide koruyucu ve diyet', status: 'in-progress', weight: 3.8, temperature: 39.1 },
  ],
};

type DemoPet = (typeof demoData.pets)[number];

const TABS = [
  { id: 'dashboard', label: 'Kontrol Paneli', icon: Home },
  { id: 'owners', label: 'Hayvan Sahipleri', icon: Users },
  { id: 'pets', label: 'Hastalar', icon: PawPrint },
  { id: 'appointments', label: 'Randevular', icon: Calendar },
  { id: 'examinations', label: 'Muayeneler', icon: Stethoscope },
  { id: 'vaccinations', label: 'Aşılar', icon: Syringe },
  { id: 'vaccine-card', label: 'Aşı Kartı', icon: CreditCard },
  { id: 'patient-report', label: 'Hasta Raporu', icon: FileText },
  { id: 'statistics', label: 'İstatistikler', icon: BarChart3 },
  { id: 'clinic-settings', label: 'Klinik Ayarları', icon: Building2 },
];

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    upcoming: 'bg-teal-50 text-teal-800 border-teal-200',
    'in-progress': 'bg-sky-50 text-sky-700 border-sky-200',
  };
  const labels: Record<string, string> = {
    confirmed: 'Onaylandı', completed: 'Tamamlandı',
    pending: 'Bekliyor', upcoming: 'Planlı', 'in-progress': 'Devam ediyor',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border ${map[status] || 'bg-slate-100 text-slate-600 border-[#E8E2D4]'}`}>
      {labels[status] || status}
    </span>
  );
};

const DemoPage: React.FC = () => {
  const todayLong = new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const todayShort = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  useSeo({ title: 'Demo | KlinikVet', path: '/demo', noindex: true });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPet, setSelectedPet] = useState<DemoPet>(demoData.pets[0]);

  const filteredPets = demoData.pets.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.breed.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ── DASHBOARD (gerçek Dashboard.tsx görünümü, örnek veri) ── */
  const renderDashboard = () => {
    const done = 7, pend = 4, canc = 1, total = done + pend + canc;
    const C = 2 * Math.PI * 50;
    const segs = [
      { label: 'Tamamlandı', v: done, color: '#557236' },
      { label: 'Bekliyor', v: pend, color: '#F59E0B' },
      { label: 'İptal', v: canc, color: '#F87171' },
    ];
    let acc = 0;
    const weekly = [14, 18, 12, 21, 17, 24];
    const maxW = Math.max(...weekly);
    const cardCls = 'rounded-2xl p-5 bg-white border border-[#E8E2D4]';
    return (
      <div className="space-y-5">
        <div className="flex items-start gap-3 p-4 rounded-xl bg-teal-50 border border-teal-200">
          <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center shrink-0 mt-0.5">
            <PawPrint className="w-3.5 h-3.5 text-teal-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-teal-900">Etkileşimli demo — örnek veri</p>
            <p className="text-[12px] text-teal-800 mt-0.5">
              Gerçek KlinikVet panelinin örnek verili önizlemesini geziyorsunuz. Hiçbir veri kaydedilmez.
            </p>
          </div>
          <Link to="/demo-request" className="shrink-0 text-[12px] font-semibold text-teal-800 hover:text-teal-900 whitespace-nowrap">
            Demo iste →
          </Link>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-1 text-teal-600">{todayLong}</p>
            <h1 className="text-[22px] lg:text-[26px] font-bold leading-tight text-[#2B2B2B]">İyi günler! 👋</h1>
            <p className="text-[13px] mt-0.5 text-[#6B6657]">Kliniğinizin bugünkü durumuna bakın.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setActiveTab('pets')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold bg-white border border-[#E8E2D4] text-[#2B2B2B]">
              <PlusCircle className="w-3.5 h-3.5" /> Hasta Ekle
            </button>
            <button onClick={() => setActiveTab('examinations')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors">
              <ClipboardPlus className="w-3.5 h-3.5" /> Yeni Muayene
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={cardCls}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B6657]">Aylık İlerleme</p>
              <span className="text-[11px] font-bold text-teal-700">73%</span>
            </div>
            <p className="text-[11px] mb-3 text-teal-700">+22 randevu bu ay · Hedef: 30</p>
            <div className="text-[44px] font-bold leading-none mb-4 text-[#2B2B2B]">73<span className="text-[24px]">%</span></div>
            <div className="relative h-2 rounded-full mb-3 overflow-hidden bg-[#F0EBE1]" role="progressbar" aria-valuenow={73} aria-valuemin={0} aria-valuemax={100} aria-label="Aylık ilerleme">
              <div className="absolute inset-y-0 left-0 rounded-full w-[73%] bg-gradient-to-r from-teal-600 to-teal-400" />
            </div>
            <div className="flex justify-between text-[10px] text-[#6B6657]"><span>0</span><span>30 hedef</span></div>
            <div className="mt-4 pt-3 flex justify-between border-t border-[#F0EBE1]">
              {[['12', 'Bugün'], [String(demoData.stats.totalPets), 'Toplam Hasta'], ['3', 'Geciken Aşı']].map(([v, l]) => (
                <div key={l} className="text-center">
                  <p className="text-[18px] font-bold text-[#2B2B2B]">{v}</p>
                  <p className="text-[10px] text-[#6B6657]">{l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={cardCls}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B6657]">Bugünkü Randevular</p>
              <button onClick={() => setActiveTab('appointments')} className="text-[11px] font-medium text-teal-700">Tümü →</button>
            </div>
            <p className="text-[11px] mb-3 text-[#6B6657]">{todayShort}</p>
            <div className="relative flex items-center justify-center" style={{ height: 130 }}>
              <svg viewBox="0 0 130 130" width="130" height="130" role="img" aria-label="Bugünkü randevu durumları">
                <g transform="rotate(-90 65 65)">
                  {segs.map(sg => {
                    const len = (sg.v / total) * C;
                    const el = (
                      <circle key={sg.label} cx="65" cy="65" r="50" fill="none" stroke={sg.color} strokeWidth="16"
                        strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} />
                    );
                    acc += len;
                    return el;
                  })}
                </g>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-[28px] font-bold leading-none text-[#2B2B2B]">{total}</p>
                <p className="text-[10px] text-[#6B6657]">Toplam</p>
              </div>
            </div>
            <div className="space-y-1.5 mt-2">
              {segs.map(r => (
                <div key={r.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
                    <span className="text-[12px] text-[#2B2B2B]">{r.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-semibold text-[#2B2B2B]">{r.v}</span>
                    <span className="text-[11px] text-[#6B6657]">({Math.round((r.v / total) * 100)}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={cardCls}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#6B6657]">Toplam Hastalar</p>
              <span className="text-[20px] font-bold text-[#2B2B2B]">{demoData.stats.totalPets}</span>
            </div>
            <div className="flex items-center gap-3 mb-3 flex-wrap text-[11px] text-[#6B6657]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-600" />Aktif {demoData.stats.totalPets}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E8E2D4]" />Sahip {demoData.stats.totalOwners}</span>
            </div>
            <div className="flex items-end justify-between gap-2 h-[140px]" role="img" aria-label="Son 6 haftalık hasta grafiği">
              {weekly.map((w, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  <div className="w-2.5 rounded-t bg-teal-600" style={{ height: `${(w / maxW) * 100}%` }} />
                  <span className="text-[10px] text-[#6B6657] mt-1">H{i + 1}</span>
                </div>
              ))}
            </div>
            <div className="pt-3 mt-1 flex justify-between border-t border-[#F0EBE1]">
              {[[String(demoData.stats.totalOwners), 'Hasta Sahibi'], [String(demoData.stats.pendingExaminations), 'Bugün Muayene'], ['5', 'Bugün Aşı']].map(([v, l]) => (
                <div key={l}>
                  <p className="text-[18px] font-bold text-[#2B2B2B]">{v}</p>
                  <p className="text-[10px] text-[#6B6657]">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 rounded-2xl overflow-hidden bg-white border border-[#E8E2D4]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0EBE1]">
              <div>
                <h2 className="text-[14px] font-bold text-[#2B2B2B]">Yaklaşan Randevular</h2>
                <p className="text-[11px] mt-0.5 text-[#6B6657]">{demoData.appointments.length} randevu planlandı</p>
              </div>
              <button onClick={() => setActiveTab('appointments')} className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-[#F7F4ED] text-teal-700 border border-[#E8E2D4]">
                Tümünü Gör
              </button>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[420px]">
                <div className="grid px-5 py-2.5 border-b border-[#F0EBE1]" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr' }}>
                  {['Hasta', 'Sahip', 'Tarih & Saat', 'Durum'].map(h => (
                    <span key={h} className="text-[10px] font-bold uppercase tracking-wider text-[#6B6657]">{h}</span>
                  ))}
                </div>
                {demoData.appointments.map(a => (
                  <div key={a.id} className="grid px-5 py-3 border-b border-[#F7F4ED] last:border-0 hover:bg-[#FAFAF8] transition-colors" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr' }}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-teal-100">
                        <PawPrint className="w-3.5 h-3.5 text-teal-700" />
                      </div>
                      <span className="text-[12px] font-semibold truncate text-[#2B2B2B]">{a.petName}</span>
                    </div>
                    <span className="text-[12px] truncate self-center text-[#6B6657]">{a.ownerName}</span>
                    <div className="self-center">
                      <p className="text-[11px] font-semibold text-[#2B2B2B]">{a.date === '2025-01-06' ? 'Bugün' : `${a.date.slice(8)} Oca`}</p>
                      <p className="text-[10px] text-[#6B6657]">{a.time}</p>
                    </div>
                    <div className="self-center"><StatusBadge status={a.status} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-2xl overflow-hidden bg-white border border-[#E8E2D4]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0EBE1]">
              <div>
                <h2 className="text-[13px] font-bold text-[#2B2B2B]">Bu Hafta Aşılar</h2>
                <p className="text-[11px] text-[#6B6657]">2 yaklaşan · <span className="text-[#B4573A]">1 gecikmiş</span></p>
              </div>
              <button onClick={() => setActiveTab('vaccinations')} className="text-[11px] font-medium text-teal-700">Tümü →</button>
            </div>
            <div className="divide-y divide-[#F0EBE1]">
              {[
                { n: 'Kuduz', p: 'Simba · Mehmet Kaya', d: '2g', late: false },
                { n: 'Karma Aşı', p: 'Pamuk · Ayşe Demir', d: '5g', late: false },
                { n: 'Karma Aşı', p: 'Karamel · Ahmet Yılmaz', d: '12g önce', late: true },
              ].map(v => (
                <div key={v.n + v.p} className="flex items-center gap-3 px-5 py-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${v.late ? 'bg-[#FAECE7]' : 'bg-amber-50'}`}>
                    <Syringe className={`w-3.5 h-3.5 ${v.late ? 'text-[#B4573A]' : 'text-amber-700'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold truncate text-[#2B2B2B]">{v.n}</p>
                    <p className="text-[10px] truncate text-[#6B6657]">{v.p}</p>
                  </div>
                  <p className={`text-[11px] font-bold shrink-0 ${v.late ? 'text-[#B4573A]' : 'text-amber-700'}`}>{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPlaceholder = (title: string) => (
    <div className="rounded-2xl bg-white border border-[#E8E2D4] p-10 text-center">
      <p className="text-[15px] font-bold text-[#2B2B2B]">{title}</p>
      <p className="text-[13px] text-[#6B6657] mt-1">Bu modül demoda örnek ekran içermiyor. Gerçek panelde tüm modüller açıktır.</p>
      <Link to="/demo-request" className="inline-block mt-4 text-[12px] font-semibold text-teal-800 hover:text-teal-900">Gerçek hesap iste →</Link>
    </div>
  );

  /* ── PETS ── */
  const renderPets = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input type="text" placeholder="Hayvan, ırk veya sahip ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-2 rounded-xl border border-[#E8E2D4] text-[13px] text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-700/20 focus:border-teal-600 bg-white" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E8E2D4] bg-white text-[13px] font-medium text-slate-600 hover:border-slate-300 transition-colors">
          <Filter className="w-3.5 h-3.5" /> Filtrele
        </button>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-700 text-white text-[13px] font-medium hover:bg-teal-800 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Yeni Hayvan
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredPets.map(pet => (
          <div key={pet.id} className="p-4 rounded-2xl bg-white border border-[#E8E2D4] hover:border-slate-300 hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${pet.avatarColor} flex items-center justify-center text-white font-bold text-[13px] shrink-0`}>
                {pet.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-slate-900">{pet.name}</p>
                <p className="text-[11px] text-slate-500">{pet.breed}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[10px]">
              <div className="text-center p-2 rounded-lg bg-[#F7F4ED]">
                <div className="font-bold text-slate-900">{pet.age} yaş</div>
                <div className="text-slate-500">Yaş</div>
              </div>
              <div className="text-center p-2 rounded-lg bg-[#F7F4ED]">
                <div className="font-bold text-slate-900">{pet.weight} kg</div>
                <div className="text-slate-500">Kilo</div>
              </div>
              <div className="text-center p-2 rounded-lg bg-[#F7F4ED]">
                <div className="font-bold text-slate-900">{pet.gender}</div>
                <div className="text-slate-500">Cinsiyet</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#F0EBE1] flex items-center justify-between">
              <span className="text-[11px] text-slate-500">{pet.owner}</span>
              <div className="flex gap-1">
                <button className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"><Eye className="w-3.5 h-3.5" /></button>
                <button className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"><Edit className="w-3.5 h-3.5" /></button>
                <button className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── OWNERS ── */
  const renderOwners = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input type="text" placeholder="Sahip ara..." className="w-full pl-8 pr-3 py-2 rounded-xl border border-[#E8E2D4] text-[13px] placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-700/20 bg-white" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-700 text-white text-[13px] font-medium hover:bg-teal-800 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Yeni Sahip
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {demoData.owners.map(owner => (
          <div key={owner.id} className="p-5 rounded-2xl bg-white border border-[#E8E2D4] hover:border-slate-300 hover:shadow-md transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center text-white font-bold text-[14px] shrink-0">
                {owner.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="text-[14px] font-bold text-slate-900">{owner.name}</p>
                <span className="inline-flex px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200 text-[10px] font-bold">
                  {owner.petCount} hayvan
                </span>
              </div>
            </div>
            <div className="space-y-2 text-[12px]">
              <a href={`tel:${owner.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
                <Phone className="w-3.5 h-3.5 text-slate-500" /> {owner.phone}
              </a>
              <a href={`mailto:${owner.email}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 truncate">
                <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {owner.email}
              </a>
              <div className="flex items-start gap-2 text-slate-600">
                <MapPin className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" /> {owner.address}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── APPOINTMENTS ── */
  const renderAppointments = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input type="text" placeholder="Randevu ara..." className="w-full pl-8 pr-3 py-2 rounded-xl border border-[#E8E2D4] text-[13px] placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-700/20 bg-white" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E8E2D4] bg-white text-[13px] font-medium text-slate-600 hover:border-slate-300 transition-colors">
          <Filter className="w-3.5 h-3.5" /> Filtrele
        </button>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-700 text-white text-[13px] font-medium hover:bg-teal-800 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Yeni Randevu
        </button>
      </div>
      <div className="space-y-2">
        {demoData.appointments.map(a => (
          <div key={a.id} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-[#E8E2D4] hover:border-slate-300 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-[#F7F4ED] border border-[#E8E2D4] flex flex-col items-center justify-center shrink-0">
              <span className="text-[10px] font-bold text-slate-700">{a.date.slice(8)}</span>
              <span className="text-[9px] text-slate-500">Oca</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[13px] font-bold text-slate-900">{a.petName}</p>
                <StatusBadge status={a.status} />
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">{a.ownerName} · {a.type}</p>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[13px] font-bold text-slate-900">{a.time}</div>
              <div className="text-[11px] text-slate-500">{a.notes}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── EXAMINATIONS ── */
  const renderExaminations = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input type="text" placeholder="Muayene ara..." className="w-full pl-8 pr-3 py-2 rounded-xl border border-[#E8E2D4] text-[13px] placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-700/20 bg-white" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-700 text-white text-[13px] font-medium hover:bg-teal-800 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Yeni Muayene
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {demoData.examinations.map((ex, i) => (
          <motion.div key={ex.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="p-5 rounded-2xl bg-white border border-[#E8E2D4] hover:border-slate-300 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                  <Stethoscope className="w-5 h-5 text-rose-600" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-[14px] font-bold text-slate-900">{ex.petName}</p>
                  <p className="text-[11px] text-slate-500">{ex.ownerName} · {ex.date}</p>
                </div>
              </div>
              <StatusBadge status={ex.status} />
            </div>
            <div className="space-y-2">
              {([['Şikayet', ex.complaint], ['Tanı', ex.diagnosis], ['Tedavi', ex.treatment]] as const).map(([label, text]) => (
                <div key={label} className="p-2.5 rounded-xl bg-[#F7F4ED]">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-0.5">{label}</p>
                  <p className="text-[12px] text-slate-800 font-medium">{text}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#F0EBE1]">
              <div className="text-[11px]"><span className="text-slate-500">Ağırlık</span> <span className="font-bold text-slate-900 ml-1">{ex.weight} kg</span></div>
              <div className="text-[11px]"><span className="text-slate-500">Ateş</span> <span className="font-bold text-slate-900 ml-1">{ex.temperature}°C</span></div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  /* ── VACCINATIONS ── */
  const renderVaccinations = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {demoData.vaccinations.map((v, i) => (
        <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
          className="p-5 rounded-2xl bg-white border border-[#E8E2D4] hover:border-slate-300 transition-colors">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${v.status === 'completed' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                {v.status === 'completed'
                  ? <CheckCircle className="w-4.5 h-4.5 text-emerald-600" />
                  : <Clock className="w-4.5 h-4.5 text-amber-600" />
                }
              </div>
              <div>
                <p className="text-[13px] font-bold text-slate-900">{v.vaccine}</p>
                <p className="text-[11px] text-slate-500">{v.petName}</p>
              </div>
            </div>
            <StatusBadge status={v.status} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="p-2 rounded-lg bg-[#F7F4ED]">
              <div className="text-slate-500">Uygulama</div>
              <div className="font-bold text-slate-900 mt-0.5">{v.date}</div>
            </div>
            <div className="p-2 rounded-lg bg-[#F7F4ED]">
              <div className="text-slate-500">Sonraki doz</div>
              <div className="font-bold text-teal-700 mt-0.5">{v.nextDate}</div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  /* ── VACCINE CARD ── */
  const renderVaccineCard = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E8E2D4] bg-white text-[13px] font-medium text-slate-600 hover:border-slate-300 transition-colors">
          <Download className="w-3.5 h-3.5" /> PDF İndir
        </button>
        <button className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E8E2D4] bg-white text-[13px] font-medium text-slate-600 hover:border-slate-300 transition-colors">
          <Printer className="w-3.5 h-3.5" /> Yazdır
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {demoData.pets.map(pet => (
          <button key={pet.id} onClick={() => setSelectedPet(pet)}
            className={`p-3 rounded-xl border-2 text-left transition-all ${
              selectedPet?.id === pet.id ? 'border-teal-500 bg-teal-50' : 'border-[#E8E2D4] bg-white hover:border-slate-300'
            }`}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${pet.avatarColor} flex items-center justify-center text-white text-[11px] font-bold shrink-0`}>
                {pet.name[0]}
              </div>
              <div className="min-w-0">
                <p className="text-[12px] font-bold text-slate-900 truncate">{pet.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{pet.breed}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {selectedPet && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-white border border-[#E8E2D4] overflow-hidden">
          <div className="bg-teal-800 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-teal-200">Aşı Kartı</p>
                <h3 className="text-[24px] font-bold mt-1">{selectedPet.name}</h3>
                <p className="text-[13px] text-teal-100 mt-1">{selectedPet.breed} · {selectedPet.age} yaş · {selectedPet.gender}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-teal-200">Sahip</p>
                <p className="text-[14px] font-semibold text-white mt-0.5">{selectedPet.owner}</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <h4 className="text-[13px] font-bold text-slate-900">Aşı Geçmişi</h4>
            {demoData.vaccinations.filter(v => v.petId === selectedPet.id).length === 0 ? (
              <div className="text-center py-8 text-[13px] text-slate-500 border border-dashed border-[#E8E2D4] rounded-xl">
                Bu hayvan için aşı kaydı yok
              </div>
            ) : (
              demoData.vaccinations.filter(v => v.petId === selectedPet.id).map(v => (
                <div key={v.id} className="flex items-center justify-between p-3.5 rounded-xl border border-[#E8E2D4]">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${v.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                      {v.status === 'completed' ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-slate-900">{v.vaccine}</p>
                      <p className="text-[11px] text-slate-500">Uygulama: {v.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-500">Sonraki doz</p>
                    <p className="text-[13px] font-bold text-teal-700">{v.nextDate}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </div>
  );

  const activeLabel = TABS.find(t => t.id === activeTab)?.label ?? '';
  const sideItem = (tab: (typeof TABS)[number]) => {
    const Icon = tab.icon;
    const active = activeTab === tab.id;
    return (
      <li key={tab.id}>
        <button onClick={() => { setActiveTab(tab.id); setMenuOpen(false); }}
          aria-current={active ? 'page' : undefined}
          className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[13px] font-medium leading-tight transition-all ${
            active ? 'bg-teal-600/40 text-teal-100' : 'text-teal-200 hover:bg-white/5'
          }`}>
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${active ? 'bg-teal-600/50 text-teal-100' : 'bg-white/10 text-teal-200'}`}>
            <Icon className="h-3.5 w-3.5" strokeWidth={active ? 2.5 : 2} />
          </span>
          <span className="min-w-0 flex-1">{tab.label}</span>
        </button>
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F4ED] antialiased">
      {/* Üst çubuk */}
      <header className="sticky top-0 z-50 h-16 bg-[#F7F4ED] border-b border-[#E8E2D4]">
        <div className="h-full flex items-center justify-between px-4 lg:px-6 gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button onClick={() => setMenuOpen(o => !o)} aria-label="Menüyü aç" className="lg:hidden shrink-0 p-2 rounded-xl hover:bg-[#F0EBE1] text-slate-700">
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" aria-label="Ana sayfa" className="hidden sm:flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-slate-900 mr-2">
              <ArrowLeft className="w-4 h-4" /> Ana sayfa
            </Link>
            <div className="relative w-full max-w-[14rem] sm:max-w-[16rem] lg:max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
              <input type="text" readOnly placeholder="Hızlı ara…" aria-label="Hızlı ara"
                className="w-full rounded-xl py-1.5 pl-9 pr-14 text-[13px] outline-none bg-white border border-[#E8E2D4] text-[#2B2B2B] placeholder-slate-500" />
              <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex px-1.5 py-0.5 text-[10px] font-bold text-[#6B6657] bg-white border border-[#E8E2D4] rounded pointer-events-none">⌘K</kbd>
            </div>
            <span className="px-2 py-0.5 rounded-md bg-teal-50 text-teal-800 border border-teal-200 text-[10px] font-bold uppercase tracking-wider">Demo</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/demo-request" className="hidden md:inline-flex text-[12px] font-medium text-teal-800 hover:text-teal-900 px-2 py-1.5">Demo iste</Link>
            <span className="w-8 h-8 rounded-xl border border-[#E8E2D4] flex items-center justify-center text-[#6B6657]" aria-hidden="true">
              <Moon className="w-4 h-4" />
            </span>
            <div className="flex items-center gap-2 px-2 py-1.5">
              <div className="hidden md:block text-right">
                <p className="text-[13px] font-semibold text-[#2B2B2B] leading-tight">Dr. Ayşe Kurt</p>
                <p className="text-[11px] text-[#6B6657] leading-tight">Örnek Veteriner Kliniği</p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0">AK</div>
            </div>
            <Link to="/login" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-[12px] font-semibold hover:bg-teal-800 transition-colors">
              Giriş Yap <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </header>

      {/* Kenar çubuğu */}
      <div className={`fixed top-16 bottom-0 w-60 z-40 lg:w-52 lg:left-3 lg:bottom-3 lg:top-[4.5rem] transition-[left] duration-300 ${menuOpen ? 'left-0' : '-left-64'} lg:left-3`}>
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl bg-teal-800">
          <div className="flex shrink-0 items-center gap-2.5 px-4 py-3.5 border-b border-white/10">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-600">
              <img src="/brand-symbol.png" alt="" className="h-5 w-auto" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold leading-tight tracking-tight text-white">KlinikVet</p>
              <p className="text-[10px] font-medium mt-0.5 text-teal-200">Klinik Paneli</p>
            </div>
          </div>
          <div className="shrink-0 px-4 pt-4 pb-1.5">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-teal-300">Modüller</p>
          </div>
          <nav aria-label="Demo modülleri" className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
            <ul className="space-y-0.5">{TABS.map(sideItem)}</ul>
          </nav>
          <div className="shrink-0 px-3 py-3 border-t border-white/10">
            <Link to="/demo-request" className="block rounded-xl px-3 py-2 text-center text-[11px] font-medium border border-teal-600/50 bg-teal-600/20 text-teal-100 hover:bg-teal-600/30 transition-colors">
              Gerçek hesap iste →
            </Link>
          </div>
        </div>
      </div>

      <main className="pl-4 pr-4 lg:pl-[15.25rem] lg:pr-6 py-5 min-w-0" aria-label={activeLabel}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'owners' && renderOwners()}
        {activeTab === 'pets' && renderPets()}
        {activeTab === 'appointments' && renderAppointments()}
        {activeTab === 'examinations' && renderExaminations()}
        {activeTab === 'vaccinations' && renderVaccinations()}
        {activeTab === 'vaccine-card' && renderVaccineCard()}
        {['patient-report', 'statistics', 'clinic-settings'].includes(activeTab) && renderPlaceholder(activeLabel)}
      </main>
    </div>
  );
};

export default DemoPage;
